/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalStartResearch } from "../funcs/mcpApprovalStartResearch.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ResearchReport } from "../models/components/researchreport.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  StartMcpResearchRequest,
  StartMcpResearchSecurity,
} from "../models/operations/startmcpresearch.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type StartMcpResearchMutationVariables = {
  request: StartMcpResearchRequest;
  security?: StartMcpResearchSecurity | undefined;
  options?: RequestOptions;
};

export type StartMcpResearchMutationData = ResearchReport;

export type StartMcpResearchMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * startResearch mcpApproval
 *
 * @remarks
 * Start a research-agent run for an approval request. The agent searches the web and reads pages about the server's vendor, then files a cited report; it never decides. Runs are additive — a re-run adds a report rather than replacing one — and at most one run per request is in flight at a time: starting while one runs returns the running report.
 */
export function useStartMcpResearchMutation(
  options?: MutationHookOptions<
    StartMcpResearchMutationData,
    StartMcpResearchMutationError,
    StartMcpResearchMutationVariables
  >,
): UseMutationResult<
  StartMcpResearchMutationData,
  StartMcpResearchMutationError,
  StartMcpResearchMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildStartMcpResearchMutation(client, options),
    ...options,
  });
}

export function mutationKeyStartMcpResearch(): MutationKey {
  return ["@gram/client", "mcpApproval", "startResearch"];
}

export function buildStartMcpResearchMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: StartMcpResearchMutationVariables,
  ) => Promise<StartMcpResearchMutationData>;
} {
  return {
    mutationKey: mutationKeyStartMcpResearch(),
    mutationFn: function startMcpResearchMutationFn({
      request,
      security,
      options,
    }): Promise<StartMcpResearchMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalStartResearch(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
